import { ref, uploadBytes, UploadTask } from "@firebase/storage";
import {
  deleteObject,
  FirebaseStorage,
  getDownloadURL,
  uploadBytesResumable,
} from "firebase/storage";
import { storage, storageImport } from "../../firebase";

class FileService {
  public async downloadFrom(url: string, type: string = "image/png") {
    if (!url) return;

    const name = this.getName(url) || "";
    return fetch(url)
      .then((response) => response.blob())
      .then(
        (blob) =>
          new File([blob], name, {
            type,
          })
      );
  }

  public upload(file: File, filePath: string): Promise<string> {
    return this.uploadToBucket(storage, file, filePath);
  }

  public uploadToImport(file: File, newFileName: string): Promise<string> {
    return this.uploadToBucket(storageImport, file, newFileName);
  }

  public async uploadWithProgress(
    file: File,
    filePath: string,
    onProgress: (progress: number, uploadTask: UploadTask) => void,
    onError: (message: string) => void,
    onComplete: (downloadUrl: string) => void
  ) {
    try {
      const fileRef = ref(storage, filePath);
      const fileBuffer = await file.arrayBuffer();
      const uploadTask = uploadBytesResumable(fileRef, fileBuffer);

      uploadTask.on(
        "state_changed",
        (snapshot) => {
          const progress =
            (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          onProgress(progress, uploadTask);
        },
        (error) => {
          onError(error.message);
        },
        () => {
          getDownloadURL(uploadTask.snapshot.ref)
            .then((downloadURL) => onComplete(downloadURL))
            .catch((error) => onError(error.message));
        }
      );
    } catch (error: any) {
      onError(error.message);
    }
  }

  public toBase64(file: File): Promise<string | ArrayBuffer | null> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = () => reject();
    });
  }

  public getImageHeightAndWidth(
    imageUrl: string
  ): Promise<{ width: number; height: number }> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve({ width: img.width, height: img.height });
      img.onerror = () => reject();
      img.src = imageUrl;
    });
  }

  public deleteFile(filePath: string) {
    const fileRef = ref(storage, filePath);
    return deleteObject(fileRef);
  }

  public getPathFromUrl(url: string): string {
    const onlyURI = url.split("?")[0];
    const slices = onlyURI.split("/");
    if (!slices || !slices.length) return "";
    return decodeURIComponent(slices.pop() || "");
  }

  private getName(url: string) {
    const onlyURI = decodeURIComponent(url).split("?");
    const slices = onlyURI[0].split("/");
    return slices.pop();
  }

  private uploadToBucket(
    bucket: FirebaseStorage,
    file: File,
    filePath: string
  ): Promise<string> {
    return file
      .arrayBuffer()
      .then((buffer) => {
        const fileRef = ref(bucket, filePath);
        return uploadBytes(fileRef, buffer);
      })
      .then((snapshot) => getDownloadURL(snapshot.ref))
      .catch((error) => {
        console.error("Error on upload file:", error);
        throw error;
      });
  }
}

export default new FileService();
